import { AnalyticsReportCreationSource } from "@redotech/crdb-enums/analytics/analytics-report-creation-source";
import { AnalyticsReportStatus } from "@redotech/crdb-enums/analytics/analytics-report-status";
import { CustomAnalyticsQueryVersionSchema } from "@redotech/redo-analytics-custom-analytics/query/custom-analytics-report-query";
import { unaryRpc } from "@redotech/rpc/definition";
import * as z from "zod";

const CustomReportStatusSchema = z.enum(AnalyticsReportStatus);
const CustomReportCreationSourceSchema = z.enum(AnalyticsReportCreationSource);
const IsoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const ListCustomReportsCursorSchema = z.object({
  createdAt: z.coerce.date(),
  rowId: z.string().regex(/^\d+$/),
});

export const listCustomReports = unaryRpc({
  name: "listCustomReports" as const,
  input: z.object({
    cursor: ListCustomReportsCursorSchema.optional(),
    idSearch: z.string().trim().max(120).optional(),
    teamSearch: z.string().trim().max(200).optional(),
    createdBySearch: z.string().trim().max(200).optional(),
    creationSource: CustomReportCreationSourceSchema.optional(),
    status: CustomReportStatusSchema.optional(),
    createdAtStart: IsoDateSchema.optional(),
    createdAtEnd: IsoDateSchema.optional(),
    limit: z.number().int().min(1).max(500).default(250),
  }),
  output: z.object({
    reports: z.array(
      z.object({
        rowId: z.string(),
        publicId: z.string(),
        teamId: z.string(),
        teamName: z.string().nullable(),
        createdByUserId: z.string().nullable(),
        createdByName: z.string().nullable(),
        createdByEmail: z.string().nullable(),
        title: z.string().nullable(),
        titleManuallyEdited: z.boolean(),
        status: CustomReportStatusSchema,
        creationSource: CustomReportCreationSourceSchema,
        queryVersion: CustomAnalyticsQueryVersionSchema.nullable(),
        requestedQuestion: z.string().nullable(),
        failureCode: z.string().nullable(),
        agentRunCount: z.number().int().min(0),
        revisionCount: z.number().int().min(0),
        createdAt: z.date(),
        updatedAt: z.date(),
      }),
    ),
    totalCount: z.number().int().min(0),
    nextCursor: ListCustomReportsCursorSchema.nullable(),
    limit: z.number().int().min(1).max(500),
  }),
});

export type ListCustomReportsInput = z.infer<typeof listCustomReports.input>;
export type ListCustomReportsOutput = z.infer<typeof listCustomReports.output>;
export type ListCustomReportsCursor = z.infer<
  typeof ListCustomReportsCursorSchema
>;
